/**
 * Anthra AI — Error boundaries
 *
 * THE ONLY SANCTIONED CLASS COMPONENTS in the codebase (§13 React, AGENTS.md §4).
 * React has no hook equivalent of componentDidCatch, so this file is exempted in
 * eslint.config.js and in .cursor/rules/10-frontend.mdc.
 *
 * WHY BOUNDARIES MATTER
 *   Without one, a render-time exception unmounts the entire tree and the user
 *   gets a blank white page with no explanation and no way forward. With one,
 *   they get a scoped message, a retry, and a reference to quote to support.
 *
 * WHERE TO PUT THEM — one boundary around the whole app is not enough:
 *   - RootErrorBoundary   in src/app/App.tsx, the last line of defence
 *   - RouteErrorBoundary  per route, so a broken page keeps the shell usable
 *   - FeatureErrorBoundary around any independently-failing widget, so a broken
 *     analytics panel does not take down the invoice the user came to pay
 */

import { track } from "@anthra/platform/analytics"
import { toDisplayable } from "@anthra/platform/errors"
import { logger } from "@anthra/platform/logger"
import { Component, type ErrorInfo, type ReactNode } from "react"

/**
 * NO UI-LIBRARY IMPORT HERE, DELIBERATELY.
 *
 * This used to render `<Button>` from the component library. An error boundary
 * that depends on the component library cannot render when the component
 * library is what threw — the fallback crashes too, React unmounts the tree, and
 * the user gets a white screen instead of the recovery UI this file exists to
 * provide.
 *
 * So the fallback uses plain elements and inline-safe classes only. It is the
 * one place in the app where that is the right call.
 */

type Props = {
	children: ReactNode
	/** Custom fallback. Receives the error and a reset callback. */
	fallback?: (error: Error, reset: () => void) => ReactNode
	/** Identifies which boundary caught it, so logs say where. */
	name?: string
	/** Changing any of these resets the boundary — usually the route key. */
	resetKeys?: unknown[]
	onError?: (error: Error, info: ErrorInfo) => void
}

type State = {
	error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
	override state: State = { error: null }

	static getDerivedStateFromError(error: Error): State {
		return { error }
	}

	override componentDidCatch(error: Error, info: ErrorInfo): void {
		// A render crash is always an error-level event: the user saw a broken UI.
		logger.error(
			"react render error",
			{
				boundary: this.props.name ?? "unnamed",
				componentStack: String(info.componentStack ?? ""),
			},
			error,
		)
		track("error_shown", { error_code: "render_error", is_retryable: true })
		this.props.onError?.(error, info)
	}

	override componentDidUpdate(prev: Props): void {
		// Without this, navigating away from a crashed route leaves the fallback
		// rendered forever — the boundary has no idea the route changed.
		if (this.state.error === null) return
		if (prev.resetKeys === this.props.resetKeys) return

		const changed =
			prev.resetKeys?.length !== this.props.resetKeys?.length ||
			prev.resetKeys?.some((k, i) => !Object.is(k, this.props.resetKeys?.[i]))

		if (changed) this.reset()
	}

	reset = (): void => {
		this.setState({ error: null })
	}

	override render(): ReactNode {
		const { error } = this.state
		if (error === null) return this.props.children

		if (this.props.fallback) return this.props.fallback(error, this.reset)

		return (
			<DefaultFallback
				error={error}
				onReset={this.reset}
			/>
		)
	}
}

function DefaultFallback({ error, onReset }: { error: Error; onReset: () => void }) {
	const displayable = toDisplayable(error)

	return (
		<div
			role="alert"
			className="flex min-h-[320px] flex-col items-center justify-center gap-4 p-8 text-center"
		>
			<div className="space-y-2">
				<h2 className="text-foreground text-lg font-semibold">{displayable.title}</h2>
				<p className="text-muted max-w-md text-sm">{displayable.message}</p>
			</div>

			{displayable.correlationId ? (
				<p className="text-muted text-xs">
					Reference: <code className="font-mono">{displayable.correlationId}</code>
				</p>
			) : null}

			<div className="flex gap-2">
				<button
					type="button"
					onClick={onReset}
					className="bg-primary text-background rounded-md px-4 py-2 text-sm font-medium"
				>
					Try again
				</button>
				<button
					type="button"
					onClick={() => window.location.assign("/")}
					className="border-border rounded-md border px-4 py-2 text-sm font-medium"
				>
					Go home
				</button>
			</div>

			{import.meta.env.DEV ? (
				<details className="mt-4 max-w-2xl text-left">
					<summary className="text-muted cursor-pointer text-xs">
						Developer detail (development only)
					</summary>
					<pre className="scrollbar-hide rounded-control bg-surface-muted text-meta mt-2 overflow-x-auto p-3">
						{error.stack ?? error.message}
					</pre>
				</details>
			) : null}
		</div>
	)
}

/** Last line of defence. Wrap the app in src/app/App.tsx. */
export function RootErrorBoundary({ children }: { children: ReactNode }) {
	return (
		<ErrorBoundary
			name="root"
			fallback={error => {
				const d = toDisplayable(error)
				return (
					<div
						role="alert"
						className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center"
					>
						<h1 className="text-xl font-semibold">{d.title}</h1>
						<p className="text-muted max-w-md text-sm">{d.message}</p>
						{d.correlationId ? (
							<p className="text-muted text-xs">
								Reference: <code className="font-mono">{d.correlationId}</code>
							</p>
						) : null}
						{/* A full reload, not a state reset: at the root the app's state is
                already suspect, and half-recovering produces stranger bugs than
                starting clean. */}
						<button
							type="button"
							onClick={() => window.location.reload()}
						>
							Reload
						</button>
					</div>
				)
			}}
		>
			{children}
		</ErrorBoundary>
	)
}

/** Per-route boundary. Keeps the shell usable when one page crashes. */
export function RouteErrorBoundary({
	children,
	routeKey,
}: {
	children: ReactNode
	routeKey: string
}) {
	return (
		<ErrorBoundary
			name={`route:${routeKey}`}
			resetKeys={[routeKey]}
		>
			{children}
		</ErrorBoundary>
	)
}

/**
 * Around an independently-failing widget. Degrades rather than removing the page:
 * a broken sidebar chart should not stop someone paying an invoice
 * (ARCHITECTURE.md §1.6, graceful degradation).
 */
export function FeatureErrorBoundary({
	children,
	feature,
}: {
	children: ReactNode
	feature: string
}) {
	return (
		<ErrorBoundary
			name={`feature:${feature}`}
			fallback={(_, reset) => (
				<div
					role="alert"
					className="border-border text-muted rounded-md border border-dashed p-4 text-center text-sm"
				>
					<p>This section could not be loaded.</p>
					<button
						type="button"
						onClick={reset}
						className="mt-2 rounded px-3 py-1 text-xs font-medium underline"
					>
						Retry
					</button>
				</div>
			)}
		>
			{children}
		</ErrorBoundary>
	)
}
